import { useQuery } from '@tanstack/react-query';
import { AnswerCard } from '../components/AnswerCard';
import { LearningRow } from '../components/VerSections';
import { useAiStatus } from '../lib/ai';
import { Glyph, AskBar, EmptyState, Label, Tag, Thinking, Title } from '@raro/ui';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../lib/api';
import { HUMAN_STATUS_LABELS, SOURCE_TYPE_LABELS, locationLabel } from '../lib/format';
import type { AiAnswer, Learning, SearchResponse } from '../lib/types';

export function MemoriaPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const org = params.get('org') ?? '';
  const project = params.get('project') ?? '';

  const status = useAiStatus();
  const learnings = useQuery({ queryKey: ['raro-learnings'], queryFn: () => api.get<Learning[]>('/learnings'), enabled: !q });
  // um id por pergunta: é por ele que a tela acompanha os passos enquanto a resposta não chega
  const progressId = useMemo(() => crypto.randomUUID(), [q, org, project]);
  const ask = useQuery({
    queryKey: ['ask', q, org, project],
    queryFn: () => api.post<AiAnswer>('/ai/ask', { question: q, organizationId: org || undefined, projectId: project || undefined, progressId }),
    enabled: q.trim().length >= 3 && Boolean(status.data?.llm),
    staleTime: Infinity,
  });
  const results = useQuery({
    queryKey: ['search', q, org, project],
    queryFn: () =>
      api.get<SearchResponse>(`/search?q=${encodeURIComponent(q)}${org ? `&organizationId=${org}` : ''}${project ? `&projectId=${project}` : ''}`),
    enabled: q.trim().length >= 2,
  });

  return (
    <div className="rr-page rr-stack rr-stack--7">
      <header className="rr-stack rr-stack--5">
        <div className="rr-stack rr-stack--2">
          <Glyph name="memoria" size={64} className="rr-page-glyph" /><Label>Memória</Label>
          <Title>o que a Raro já viu?</Title>
        </div>
        <AskBar
          key={q}
          placeholder="mostre situações em que vendedores percebiam um diferencial que o marketing não comunicava…"
          hint={project ? 'buscando só neste projeto. limpe os filtros para atravessar toda a memória.' : status.data?.llm ? 'a Machine responde com citações e mostra os trechos. cada resultado diz por que apareceu.' : 'busca híbrida: palavras + sentido. sem modelo configurado, a Machine só procura.'}
          onSubmit={(next) => setParams({ q: next, ...(org ? { org } : {}), ...(project ? { project } : {}) })}
          autoFocus
        />
        {(org || project) && (
          <button type="button" className="rr-action rr-action--bare" style={{ alignSelf: 'flex-start', fontSize: 'var(--rr-text-xs)' }} onClick={() => setParams({ q })}>
            limpar filtros e buscar em tudo
          </button>
        )}
      </header>

      {!q && learnings.isSuccess && (
        <section className="rr-section">
          <div className="rr-section__head">
            <Label>O que a Raro aprendeu</Label>
            <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>aprendizados que atravessam projetos · {learnings.data.length}</span>
          </div>
          {learnings.data.length === 0 ? <EmptyState title="nada consolidado ainda.">aprendizados com alcance “memória Raro” ou “transferível” aparecem aqui.</EmptyState> : <div>{learnings.data.map((l) => <LearningRow key={l.id} l={l} showProject />)}</div>}
        </section>
      )}
      {q && status.data?.llm && (ask.isPending ? <AskThinking id={progressId} /> : ask.isSuccess ? <AnswerCard a={ask.data} /> : ask.isError ? <p className="rr-field__error">{(ask.error as Error).message}</p> : null)}
      {q && results.isSuccess && (
        <section className="rr-section">
          <div className="rr-section__head">
            <Label>
              {results.data.hits.length} resultado{results.data.hits.length === 1 ? '' : 's'} para “{q}”
            </Label>
            <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>
              {results.data.semantic ? 'palavras + sentido' : 'só palavras (sem provedor de embeddings)'}
            </span>
          </div>
          {results.data.hits.length === 0 ? (
            <EmptyState title="nada com essas palavras.">tente outros termos. com embeddings ativos, a Machine também encontra por sentido.</EmptyState>
          ) : (
            <div>
              {results.data.hits.map((h) => (
                <article key={`${h.kind}-${h.id}`} className="rr-hit">
                  <div className="rr-hit__path">
                    <span>{h.organizationName}</span>
                    {h.projectName && (
                      <>
                        <span>/</span>
                        <Link to={`/projetos/${h.projectId}`}>{h.projectName}</Link>
                      </>
                    )}
                    <span>/</span>
                    {h.kind === 'radar'
                      ? <Link to={`/clientes/${h.organizationId}?aba=concorrentes`}>{h.sourceTitle}</Link>
                      : h.sourceId ? <Link to={`/fontes/${h.sourceId}`}>{h.sourceTitle}</Link> : <span>{h.sourceTitle}</span>}
                    {locationLabel(h.location) && (
                      <>
                        <span>·</span>
                        <span>{locationLabel(h.location)}</span>
                      </>
                    )}
                    {h.kind !== 'radar' && <><span>·</span><span>{SOURCE_TYPE_LABELS[h.sourceType]}</span></>}
                    {h.kind === 'radar' && <Tag tone="outline">{h.own ? 'publicado pela própria marca' : 'publicado pelo concorrente'} · não é evidência</Tag>}
                    {h.kind === 'radar' && h.url && <a href={h.url} target="_blank" rel="noreferrer">abrir o original</a>}
                    {h.kind === 'evidence' && <Tag tone={h.humanStatus === 'accepted' ? 'accent' : 'outline'}>evidência · {h.humanStatus ? HUMAN_STATUS_LABELS[h.humanStatus] : ''}</Tag>}
                  </div>
                  <p className="rr-hit__snippet" dangerouslySetInnerHTML={{ __html: highlight(h.snippet) }} />
                  <div className="rr-hit__why">{h.why}</div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/** Os passos da pergunta, lidos do servidor enquanto ela roda. Sem passo ainda, mostra só o primeiro, que é sempre verdade. */
function AskThinking({ id }: { id: string }) {
  const [steps, setSteps] = useState<{ kind: 'stage' | 'item'; text: string }[]>([]);
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    let alive = true, got = 0;
    const started = Date.now();
    const tick = async () => {
      setSeconds(Math.round((Date.now() - started) / 1000));
      try {
        const r = await api.get<{ events: { kind: string; text: string }[]; total: number }>(`/ai/research-progress/${id}?after=${got}`);
        if (!alive || !r.events.length) return;
        got = r.total;
        setSteps((s) => [...s, ...r.events.map((e) => ({ kind: e.kind === 'stage' ? 'stage' as const : 'item' as const, text: e.text }))]);
      } catch { /* acompanhar é enfeite: sem ele a resposta chega do mesmo jeito */ }
    };
    const timer = window.setInterval(tick, 600);
    void tick();
    return () => { alive = false; window.clearInterval(timer); };
  }, [id]);
  return <Thinking label="a Machine está respondendo" seconds={seconds} steps={steps.length ? steps : [{ kind: 'stage', text: 'procurando na memória, por palavras e por sentido' }]} />;
}

function highlight(snippet: string) {
  const escaped = snippet.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped.replace(/«/g, '<mark>').replace(/»/g, '</mark>');
}
